import { roleLabel as sharedRoleLabel } from '@/auth/roles';
import { daysUntil } from './expiry';
/** «Ольга Попова» → «ОП»; one word → its first letter. */
export function initials(name) {
    const words = name.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0)
        return '?';
    return words
        .slice(0, 2)
        .map((w) => w.charAt(0).toUpperCase())
        .join('');
}
/** Colour of the subscription state: running, ending within a week, over, never paid. */
export function subTone(expire, today) {
    if (!expire)
        return 'gray';
    const days = daysUntil(expire, today);
    if (days === null)
        return 'gray';
    if (days < 0)
        return 'red';
    return days <= 7 ? 'amber' : 'green';
}
const KIND_LABELS = {
    acquiring: 'Пополнение',
    referral: 'Реферальное начисление',
    system: 'Системное начисление',
};
/** Balance history `kind` in plain words; an unknown one is shown as is. */
export function kindLabel(kind) {
    return KIND_LABELS[kind] ?? kind;
}
/** «2026-10-18…» → «18.10.2026»: the date as the server sent it, no timezone shift. */
export function ruDate(iso) {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
    return m ? [m[3], m[2], m[1]].join('.') : iso;
}
/** A moment in the viewer's local time: «18.09.2026, 14:32». */
export function ruDateTime(iso) {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime()))
        return iso;
    return d.toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}
/** Subscription state for the chip under the name. */
export function subChip(expire, today) {
    if (!expire)
        return 'Нет подписки';
    const days = daysUntil(expire, today);
    if (days === null)
        return expire;
    if (days < 0)
        return `Истекла ${ruDate(expire)}`;
    if (days === 0)
        return 'Истекает сегодня';
    return `Активна ещё ${days} дн.`;
}
/** Currency sign from the directory; rubles are «₽» even without it, others fall back to the code. */
export function currencySymbol(code, directory) {
    return directory?.find((c) => c.code === code)?.symbol || (code === 'RUB' ? '₽' : code);
}
/** Signs of the currencies cashiers charge in; any other shows its code. */
const CHARGE_SIGNS = { RUB: '₽', USD: '$', EUR: '€', XTR: '★' };
/** What a cashier charged: «250 ★», «15,20 $» — its decimals kept; null when nothing. */
export function chargeText(amount, currency) {
    if (amount == null || !currency)
        return null;
    const n = Number(amount);
    const sign = CHARGE_SIGNS[currency] ?? currency;
    if (!Number.isFinite(n))
        return `${amount}\u00a0${sign}`;
    const decimals = Math.min(2, amount.split('.')[1]?.length ?? 0);
    const text = n.toLocaleString('ru-RU', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: 2,
    });
    return `${text}\u00a0${sign}`;
}
/** What credited a bonus, by the mark the service puts in `source`. */
const SOURCE_LABELS = {
    mailing_gift: 'Подарок из рассылки',
    welcome_gift: 'Приветственный бонус',
    auth_method_bonus: 'Бонус за привязку способа входа',
    referred_bonus: 'Бонус за приглашение',
};
/**
 * A balance movement in the card: what it was, when, through which cashier and
 * what the cashier charged; the sums credited are roubles.
 */
export function paymentRow(p) {
    const paid = chargeText(p.charge_amount, p.charge_currency);
    const named = p.source ? SOURCE_LABELS[p.source] : undefined;
    return {
        label: named ?? kindLabel(p.kind),
        sub: [
            ruDateTime(p.created_at),
            named ? null : (p.display_name ?? p.source),
            paid && `оплачено ${paid}`,
        ]
            .filter(Boolean)
            .join(' · '),
        amount: money(p.amount, '₽', true),
        bonus: p.bonus ? `${money(p.bonus, '₽', true)} бонус` : null,
    };
}
/** «1500» → «1 500 ₽»; `signed` adds «+» to income. Russian grouping, up to two decimals. */
export function money(amount, symbol, signed = false) {
    const n = Math.abs(amount).toLocaleString('ru-RU', { maximumFractionDigits: 2 });
    const sign = amount < 0 ? '−' : signed && amount > 0 ? '+' : '';
    return symbol ? `${sign}${n}\u00a0${symbol}` : `${sign}${n}`;
}
/**
 * Daily tariff comes from Users as an exact decimal string. Keep every decimal
 * the backend supplied (apart from insignificant trailing zeroes) and never
 * apply the display currency's rounding precision.
 */
export function dailyTariffText(amount, symbol = '₽') {
    const raw = amount?.trim();
    if (!raw)
        return '—';
    const match = /^([+-]?)(\d+)(?:\.(\d+))?$/.exec(raw);
    if (!match)
        return `${raw}\u00a0${symbol}`;
    const sign = match[1] === '-' ? '−' : match[1] === '+' ? '+' : '';
    const integer = (match[2].replace(/^0+(?=\d)/, '') || '0').replace(/\B(?=(\d{3})+(?!\d))/g, '\u00a0');
    const fraction = match[3]?.replace(/0+$/, '') ?? '';
    return `${sign}${integer}${fraction ? `,${fraction}` : ''}\u00a0${symbol}`;
}
/**
 * A linked sign-in method as the card shows it: Telegram by @username when the
 * source passed one (the numeric id goes under it), anything else by its
 * identifier; plus since when, and a flag when it isn't confirmed.
 */
export function methodView(m) {
    const username = typeof m.extra_data?.username === 'string' ? m.extra_data.username : null;
    const byUsername = m.auth_type === 'telegram' && !!username;
    const value = byUsername ? `@${username}` : m.identifier;
    const sub = [
        byUsername ? `id ${m.identifier}` : null,
        m.created_at ? `с ${ruDate(m.created_at)}` : null,
        m.verified === false ? 'не подтверждён' : null,
    ]
        .filter(Boolean)
        .join(' · ');
    return { value, sub };
}
/** GlobalAuth role in plain words; an unknown one is shown as is. */
export function roleLabel(role) {
    return sharedRoleLabel(role);
}
